import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bot, User, Copy, Check } from 'lucide-react';

const MessageBubble = ({ message }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const bubbleVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const isUser = message.role === 'user';
  const isError = message.isError;

  if (isUser) {
    return (
      <motion.div
        variants={bubbleVariants}
        initial="hidden"
        animate="visible"
        style={{
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <div style={{ maxWidth: '320px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-end',
            gap: '8px',
            justifyContent: 'flex-end'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #3b82f6 100%)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '16px',
                borderBottomRightRadius: '4px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                <p style={{
                  fontSize: '14px',
                  margin: '0',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-words'
                }}>
                  {message.content}
                </p>
              </div>
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                textAlign: 'right',
                padding: '0 4px'
              }}>
                {formatTimestamp(message.timestamp)}
              </div>
            </div>
            <div style={{
              width: '32px',
              height: '32px',
              background: '#3b82f6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: '0'
            }}>
              <User size={16} style={{ color: 'white' }} />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={bubbleVariants}
      initial="hidden"
      animate="visible"
      style={{
        display: 'flex',
        justifyContent: 'flex-start'
      }}
    >
      <div style={{ maxWidth: '320px' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-end',
          gap: '8px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #8b5cf6 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: '0'
          }}>
            <Bot size={16} style={{ color: 'white' }} />
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            position: 'relative'
          }}>
            <div style={{
              background: isError ? '#fef2f2' : 'white',
              border: isError ? '1px solid #fecaca' : '1px solid #e5e7eb',
              color: isError ? '#991b1b' : '#1f2937',
              padding: '8px 16px',
              borderRadius: '16px',
              borderBottomLeftRadius: '4px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              <p style={{
                fontSize: '14px',
                margin: '0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-words'
              }}>
                {message.content}
              </p>
              
              {/* Copy Button */}
              <button
                onClick={handleCopy}
                title="Copy message"
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  opacity: '0',
                  transition: 'opacity 0.2s ease',
                  padding: '4px',
                  background: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0'}
              >
                {copied ? (
                  <Check size={14} style={{ color: '#10b981' }} />
                ) : (
                  <Copy size={14} style={{ color: '#9ca3af' }} />
                )}
              </button>
            </div>
            
            {/* Sources */}
            {message.sources && message.sources.length > 0 && (
              <div style={{
                background: '#dbeafe',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                padding: '8px',
                marginTop: '4px'
              }}>
                <p style={{
                  fontSize: '12px',
                  color: '#1e40af',
                  fontWeight: '500',
                  margin: '0 0 4px 0'
                }}>
                  Sources:
                </p>
                <div>
                  {message.sources.map((source, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        background: '#60a5fa',
                        borderRadius: '50%'
                      }} />
                      <p style={{
                        fontSize: '12px',
                        color: '#2563eb',
                        margin: '0'
                      }}>
                        {source.title}
                      </p>
                      {source.score && (
                        <span style={{ fontSize: '12px', color: '#3b82f6' }}>
                          ({Math.round(source.score * 100)}% match)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              textAlign: 'left',
              padding: '0 4px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>{formatTimestamp(message.timestamp)}</span>
              {message.tokenCount && (
                <span style={{ color: '#6b7280' }}> • {message.tokenCount} tokens</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MessageBubble;
